import type { ChangeEvent } from 'react';
import {
  FileUploaderDropContainer,
  FileUploaderItem,
  InlineNotification,
  TextArea,
} from '@carbon/react';

import type { AssetListSummary, AssetNameCheck } from '../asset-list-utils';
import { ASSET_LIST_EXTENSIONS, type useAssetListUpload } from '../hooks';
import type { AssetListInput } from '../types';
import { AssetListResults } from './asset-list-results';
import styles from './asset-list-panel.module.scss';

/** Props for {@link AssetListPanel}. */
export interface AssetListPanelProps {
  /** Pasted text and the listed file. */
  input: AssetListInput;
  onTextChange: (text: string) => void;
  /** Drops the listed file and its names. */
  onRemoveFile: () => void;
  upload: ReturnType<typeof useAssetListUpload>;
  summary: AssetListSummary;
  failure: AssetNameCheck['failure'];
  /** Re-sends the names still without an answer. */
  onTryAgain: () => void;
}

/**
 * The From Asset List input: a paste box on the left and a one-file drop zone
 * on the right at equal width, an "or" between them, and what the check made
 * of the names below. The file is read on the server; its names join the
 * pasted ones in the working list.
 *
 * @param {AssetListPanelProps} props - Input, handlers, upload and check state
 * @return {JSX.Element} The panel
 */
export function AssetListPanel({
  input,
  onTextChange,
  onRemoveFile,
  upload,
  summary,
  failure,
  onTryAgain,
}: AssetListPanelProps) {
  return (
    <div className={styles.panel}>
      <div className={styles.inputs}>
        <div className={styles.paste}>
          <TextArea
            id="asset-list-paste"
            labelText="Paste Asset Names"
            helperText="Comma, semicolon, or line-separated — e.g. T-0176174, T-0175914"
            placeholder="T-0176174, T-0175914, T-0162451…"
            rows={7}
            value={input.text}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              onTextChange(event.target.value)
            }
          />
        </div>

        <div className={styles.divider} aria-hidden="true">
          or
        </div>

        <div
          className={styles.upload}
          role="group"
          aria-labelledby="asset-list-upload-label"
        >
          <p id="asset-list-upload-label" className={styles.uploadLabel}>
            Load from file
          </p>
          <FileUploaderDropContainer
            labelText="Drag and drop a CSV, TSV, TXT or XLSX file here, or click to browse"
            accept={ASSET_LIST_EXTENSIONS}
            onAddFiles={(
              _event: unknown,
              { addedFiles }: { addedFiles: File[] }
            ) => {
              if (addedFiles[0]) upload.upload(addedFiles[0]);
            }}
          />
          <p className={styles.uploadHint}>
            Accepted: .csv, .txt, .tsv, .xlsx — asset names are detected in any
            column.
          </p>
          {input.file && (
            <FileUploaderItem
              name={input.file.name}
              status="edit"
              iconDescription="Remove file"
              onDelete={onRemoveFile}
            />
          )}
          {upload.uploading !== null && (
            <FileUploaderItem name={upload.uploading} status="uploading" />
          )}
          {upload.error !== null && (
            <InlineNotification
              kind="error"
              lowContrast
              title="File not usable"
              subtitle={upload.error}
              onCloseButtonClick={upload.dismissError}
            />
          )}
        </div>
      </div>

      <AssetListResults
        summary={summary}
        failure={failure}
        onTryAgain={onTryAgain}
      />
    </div>
  );
}
